import { ref, computed } from 'vue';
import { useOrderPricing } from '@shared/composables/useOrderPricing';
/**
 * Composable für Lieferzonenvalidierung
 * Bietet debounced Validierung und State Management für Lieferadressen
 */
export function useDeliveryZoneValidation(projectId) {
    const orderPricing = useOrderPricing(projectId);
    // State
    const state = ref({
        isValidating: false,
        result: null,
        lastValidatedAddress: null
    });
    let validationTimeout = null;
    // Computed
    const isInDeliveryZone = computed(() => {
        return state.value.result?.success === true && state.value.result?.inZone === true;
    });
    const deliveryFeeCent = computed(() => {
        return state.value.result?.deliveryFeeCent || 0;
    });
    const validationError = computed(() => {
        if (!state.value.result?.success) {
            return state.value.result?.error || 'Validierung fehlgeschlagen';
        }
        if (state.value.result?.success && !state.value.result?.inZone) {
            return 'Adresse liegt außerhalb des Liefergebiets';
        }
        return null;
    });
    const canDeliver = computed(() => {
        return isInDeliveryZone.value;
    });
    // Methods
    const validateAddress = async (address) => {
        console.log('🔍 [useDeliveryZoneValidation] Starting validation for:', address);
        // Skip if same address was already validated
        if (address === state.value.lastValidatedAddress && state.value.result) {
            console.log('✅ [useDeliveryZoneValidation] Using cached result for:', address);
            return state.value.result;
        }
        state.value.isValidating = true;
        state.value.result = null;
        try {
            const result = await orderPricing.checkDeliveryZone(address);
            state.value.result = result;
            state.value.lastValidatedAddress = address;
            console.log('✅ [useDeliveryZoneValidation] Validation completed:', {
                address,
                inZone: result.inZone,
                deliveryFeeCent: result.deliveryFeeCent
            });
            return result;
        }
        catch (error) {
            const errorResult = {
                success: false,
                inZone: false,
                error: error instanceof Error ? error.message : 'Unbekannter Fehler'
            };
            state.value.result = errorResult;
            console.error('❌ [useDeliveryZoneValidation] Validation failed:', error);
            return errorResult;
        }
        finally {
            state.value.isValidating = false;
        }
    };
    const validateAddressDebounced = (address, delay = 800) => {
        return new Promise((resolve) => {
            // Clear existing timeout
            if (validationTimeout) {
                clearTimeout(validationTimeout);
            }
            // Set new timeout
            validationTimeout = setTimeout(async () => {
                const result = await validateAddress(address);
                resolve(result);
            }, delay);
        });
    };
    const clearValidation = () => {
        if (validationTimeout) {
            clearTimeout(validationTimeout);
            validationTimeout = null;
        }
        state.value = {
            isValidating: false,
            result: null,
            lastValidatedAddress: null
        };
    };
    // Utility functions
    const formatAddress = (street, postalCode, city) => {
        return `${street.trim()}, ${postalCode.trim()} ${city.trim()}`;
    };
    const isValidGermanPostalCode = (postalCode) => {
        return /^\d{5}$/.test(postalCode.trim());
    };
    const isValidAddressData = (street, postalCode, city) => {
        return street.trim().length > 0 &&
            city.trim().length > 0 &&
            isValidGermanPostalCode(postalCode);
    };
    return {
        // State
        state: computed(() => state.value),
        isValidating: computed(() => state.value.isValidating),
        validationResult: computed(() => state.value.result),
        // Computed
        isInDeliveryZone,
        deliveryFeeCent,
        validationError,
        canDeliver,
        // Methods
        validateAddress,
        validateAddressDebounced,
        clearValidation,
        // Utilities
        formatAddress,
        isValidGermanPostalCode,
        isValidAddressData
    };
}
